import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, useTheme, useMediaQuery, ThemeProvider, Link } from '@mui/material';
import { ModalHot } from '../ModalHot';
import { GeneralModal } from '../GeneralModal';
import { OtpInputHot } from '../OtpInputHot';
import { ButtonHot } from '../ButtonHot';
import { TextHot } from '../TextHot';
import Loader from '../loader';
import { useGenerateOtpMutation, useValidateOtpMutation } from '../../../redux/api/users.api';
import { executeWithCaptcha, generateTestId } from '../../../app/functions';
import Recaptcha, { RECAPTCHA_ACTIONS } from '../../services/google/recaptcha';
import { appTheme, colors } from 'common/theme';
import { padId } from 'pages/personal-zone/utils/functions';
import whatsappIcon from 'assets/icons/new_whatsapp.svg';

const getOtpMessage = (response) => response?.message || response?.data?.message || '';

const getValidateOtpErrorMessage = (error, fallbackText) => (
    error?.content
    || error?.data?.content
    || error?.data
    || fallbackText
);

const shouldShowValidateOtpErrorModal = (error) => {
    const status = Number(error?.status ?? error?.statusCode);
    return status === 417 || status === 500;
};

export const PhoneValidationModal = ({
    open,
    onClose,
    phone,
    userId,
    email,
    initialOtpResponse,
    onVerificationSuccess, 
    process = 'register',
    testID = 'phone-validation-modal'
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));

    const text = useSelector((state) => state.config.Translations);
    const parameters = useSelector((state) => state.config.Parameters);
    const lockDown = useSelector((state) => state.config.LockDownErr);

    const [generateOtpMutation] = useGenerateOtpMutation();
    const [validateOtpMutation, { isLoading: isValidateOtpLoading }] = useValidateOtpMutation();
    const [generateOtpErrorMessage, setGenerateOtpErrorMessage] = useState('');
    const [generateOtpErrorModalOpen, setGenerateOtpErrorModalOpen] = useState(false);
    const [validateOtpErrorMessage, setValidateOtpErrorMessage] = useState('');
    const [validateOtpErrorModalOpen, setValidateOtpErrorModalOpen] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isProcessingVerification, setIsProcessingVerification] = useState(false);
    const [firstTry, setFirstTry] = useState(true);
    const [disableMainBtn, setDisableMainBtn] = useState(false);
    const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
    const [otpError, setOtpError] = useState('');
    const [sendTimer, setSendTimer] = useState(false);
    const [maxOtpAttemptsReached, setMaxOtpAttemptsReached] = useState(false);
    const [countdown, setCountdown] = useState(5);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [otpResponseData, setOtpResponseData] = useState(null);
    const [loginMessage, setLoginMessage] = useState('');

    const canSendEmailOtp = otpResponseData?.isEmailValid ?? otpResponseData?.data?.isEmailValid ?? false;

    const inputRefs = useRef([]);
    const countdownRef = useRef(null);
    const handleSubmitWithOtpRef = useRef(null);
    const validateOtpInFlightRef = useRef(false);
    const resendOtpInFlightRef = useRef(false);
    const autoSubmitOtpTimeoutRef = useRef(null);

    const scheduleOtpAutoSubmit = useCallback((run, delayMs) => {
        if (autoSubmitOtpTimeoutRef.current) {
            clearTimeout(autoSubmitOtpTimeoutRef.current);
        }
        autoSubmitOtpTimeoutRef.current = setTimeout(() => {
            autoSubmitOtpTimeoutRef.current = null;
            run();
        }, delayMs);
    }, []);

    const resetState = useCallback(() => {
        if (autoSubmitOtpTimeoutRef.current) {
            clearTimeout(autoSubmitOtpTimeoutRef.current);
            autoSubmitOtpTimeoutRef.current = null;
        }
        setOtpDigits(['', '', '', '', '', '']);
        setOtpError('');
        setFirstTry(true);
        setMaxOtpAttemptsReached(false);
        setDisableMainBtn(false);
        setIsLoading(false);
        setIsProcessingVerification(false);
        setFocusedIndex(0);
        const countdownValue = parseInt(parameters?.sms_verification_send_code_again__visible_in_sec) || 5;
        setCountdown(countdownValue);
        setSendTimer(false);
        validateOtpInFlightRef.current = false;
        resendOtpInFlightRef.current = false;
    }, [parameters]);

    // סנכרון התגובה הראשונית
    useEffect(() => {
        if (open && initialOtpResponse) {
            setOtpResponseData(initialOtpResponse);
            resetState();
            setLoginMessage(getOtpMessage(initialOtpResponse));
        }
    }, [open, initialOtpResponse, resetState]);

    useEffect(() => {
        if (!open) {
            resetState();
            setOtpResponseData(null);
            setLoginMessage('');
        }
    }, [open, resetState]);

    useEffect(() => {
        if (countdown > 0) {
            countdownRef.current = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
        } else if (countdown === 0 && !sendTimer) {
            setSendTimer(true);
            resendOtpInFlightRef.current = false;
        }
        return () => clearTimeout(countdownRef.current);
    }, [countdown, sendTimer]);

    const focusInput = (index) => {
        if (inputRefs.current[index]) {
            try {
                const input = inputRefs.current[index].querySelector?.('input') || inputRefs.current[index];
                input?.focus();
                input?.select();
            } catch (e) {}
        }
    };

    useEffect(() => {
        if (!open) return;
        
        let lastCheckedValue = '';
        const checkAutofill = () => {
            // Check all inputs for autofill/paste content
            let allDigits = '';
            let foundDigits = false;
            
            for (let i = 0; i < 6; i++) {
                const inputElement = inputRefs.current[i]?.querySelector?.('input') || inputRefs.current[i];
                if (!inputElement) continue;
                
                const value = inputElement.value.replace(/\D/g, '');
                if (value.length > 0) {
                    foundDigits = true;
                    // If first input has multiple digits (pasted), use that
                    if (i === 0 && value.length > 1) {
                        allDigits = value.slice(0, 6);
                        break;
                    }
                    // Otherwise collect digits from all inputs
                    allDigits += value;
                }
            }
            
            // If we found digits and they're different from last check
            if (foundDigits && allDigits !== lastCheckedValue && allDigits.length > 0) {
                lastCheckedValue = allDigits;
                const digits = allDigits.slice(0, 6);
                
                // Fill digits left to right
                const newDigits = ['', '', '', '', '', ''];
                digits.split('').forEach((d, i) => { 
                    if (i < 6) newDigits[i] = d; 
                });
                
                setOtpDigits(newDigits);
                
                // Focus the next empty input or the last one if all filled
                const nextIndex = Math.min(digits.length, 5);
                setTimeout(() => focusInput(nextIndex), 100);
                
                // Auto-submit if we have 6 digits
                if (digits.length === 6 && handleSubmitWithOtpRef.current) {
                    scheduleOtpAutoSubmit(() => handleSubmitWithOtpRef.current(digits), 300);
                }
            }
        };
        
        const interval = setInterval(checkAutofill, 200);
        return () => clearInterval(interval);
    }, [open, scheduleOtpAutoSubmit]);

    const handleSubmitWithOtp = async (otpValue) => {
        if (autoSubmitOtpTimeoutRef.current) {
            clearTimeout(autoSubmitOtpTimeoutRef.current);
            autoSubmitOtpTimeoutRef.current = null;
        }

        if (
            validateOtpInFlightRef.current ||
            disableMainBtn ||
            isLoading ||
            isValidateOtpLoading ||
            isProcessingVerification
        ) {
            return;
        }

        if (otpValue.length !== 6) {
            setOtpError(text?.['phone_validation_err'] || 'אנא הזן 6 ספרות');
            setFirstTry(false);
            return;
        }

        validateOtpInFlightRef.current = true;
        setIsLoading(true);
        setIsProcessingVerification(true);
        setDisableMainBtn(true);

        const token = otpResponseData?.validationToken || otpResponseData?.data?.validationToken || otpResponseData?.token || otpResponseData?.validation_token;
        const sessionId = otpResponseData?.sessionId || otpResponseData?.data?.sessionId || otpResponseData?.session_id;

        executeWithCaptcha(async (captchaToken) => {
            try {
                const result = await validateOtpMutation({
                    userId: String(userId),
                    otpCode: otpValue,
                    token: token,
                    sessionId: sessionId,
                    'g-recaptcha-response': captchaToken
                });

                if (result.error) {
                    const errorMessage = getValidateOtpErrorMessage(
                        result.error,
                        text?.['phone_validation_err'],
                    );

                    if (shouldShowValidateOtpErrorModal(result.error)) {
                        setValidateOtpErrorMessage(errorMessage);
                        setValidateOtpErrorModalOpen(true);
                        setOtpError('');
                    } else {
                        setOtpError(errorMessage);
                        if (errorMessage.includes?.('חריגה')) {
                            setMaxOtpAttemptsReached(true);
                        }
                        setFirstTry(false);
                    }

                    setDisableMainBtn(false);
                    setIsLoading(false);
                    setIsProcessingVerification(false);
                    return;
                }

                if (onVerificationSuccess) {
                    try {
                        await onVerificationSuccess(result.data);
                    } catch (verificationError) {
                        setIsProcessingVerification(false);
                        setIsLoading(false);
                        setDisableMainBtn(false);
                        return;
                    }
                }
                setIsProcessingVerification(false);
            } catch (error) {
                const errorMessage = getValidateOtpErrorMessage(
                    error,
                    text?.['phone_validation_err'],
                );

                if (shouldShowValidateOtpErrorModal(error)) {
                    setValidateOtpErrorMessage(errorMessage);
                    setValidateOtpErrorModalOpen(true);
                    setOtpError('');
                } else {
                    setOtpError(errorMessage);
                    if (errorMessage.includes?.('חריגה')) {
                        setMaxOtpAttemptsReached(true);
                    }
                    setFirstTry(false);
                }

                setDisableMainBtn(false);
                setIsLoading(false);
                setIsProcessingVerification(false);
            } finally {
                validateOtpInFlightRef.current = false;
                setIsLoading(false);
            }
        }, RECAPTCHA_ACTIONS.VERIFY_CODE);
    };

    useEffect(() => {
        handleSubmitWithOtpRef.current = handleSubmitWithOtp;
    }, [handleSubmitWithOtp]);

    const handleOtpChange = (index, value) => {
        const cleanValue = value.replace(/\D/g, '').slice(-1);
        const newOtpDigits = [...otpDigits];
        newOtpDigits[index] = cleanValue;
        setOtpDigits(newOtpDigits);
        setOtpError('');

        if (cleanValue && index < 5) {
            setTimeout(() => focusInput(index + 1), 50);
        }

        const otpValue = newOtpDigits.join('');
        if (otpValue.length === 6) {
            scheduleOtpAutoSubmit(() => handleSubmitWithOtp(otpValue), 300);
        }
    };

    const handlePaste = (e, index) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text');
        const digits = pastedData.replace(/\D/g, '').slice(0, 6);
        
        if (digits.length > 0) {
            const newOtpDigits = ['', '', '', '', '', ''];
            digits.split('').forEach((d, i) => {
                if (i < 6) newOtpDigits[i] = d;
            });
            setOtpDigits(newOtpDigits);
            setOtpError('');
            
            // Focus the next empty input or the last one if all filled
            const nextIndex = Math.min(digits.length, 5);
            setTimeout(() => focusInput(nextIndex), 100);
            
            // Auto-submit if we have 6 digits
            if (digits.length === 6 && handleSubmitWithOtpRef.current) {
                scheduleOtpAutoSubmit(() => handleSubmitWithOtpRef.current(digits), 300);
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
            e.preventDefault();
            focusInput(index - 1);
        }
    };

    const handleResendOtp = async ({ isEmailNeeded = false, isWhatsappNeeded = false } = {}) => {
        if (resendOtpInFlightRef.current) return;
        if (!sendTimer && !maxOtpAttemptsReached) return;

        resendOtpInFlightRef.current = true;
        setOtpDigits(['', '', '', '', '', '']);
        setOtpError('');
        setFirstTry(true);
        setMaxOtpAttemptsReached(false);
        setSendTimer(false);
        setCountdown(parseInt(parameters?.sms_verification_send_code_again__visible_in_sec) || 5);

        executeWithCaptcha(async (token) => {
            try {
                const result = await generateOtpMutation({
                    userId: padId(userId),
                    cellphone: String(phone),
                    email: email,
                    isEmailNeeded,
                    ...(isWhatsappNeeded ? { isWhatsappNeeded: true } : {}),
                    process,
                    'g-recaptcha-response': token
                });
                if (result.error){
                    const errorMessage = result?.error?.content || 'שגיאה בשליחת קוד אימות';  
                    setGenerateOtpErrorMessage(errorMessage);
                    setGenerateOtpErrorModalOpen(true);
                    return;
                }
                setOtpResponseData(result.data);
                setLoginMessage(getOtpMessage(result.data));
                setTimeout(() => focusInput(0), 200);
            } catch (err) {}
        }, RECAPTCHA_ACTIONS.RESEND_CODE);
    };

    return (
        <ModalHot open={open} onClose={onClose} testID={testID} maxWidth={600}>
            <ThemeProvider theme={appTheme}>
                <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmitWithOtp(otpDigits.join('')); }} sx={{ width: '100%', position: 'relative' }}>
                    {isProcessingVerification && (
                        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.9)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Loader />
                        </Box>
                    )}
                    <Box sx={{ mb: 4, textAlign: 'center' }}>
                        <TextHot size="xxxxl" bold="extraBold" color="primary" sx={{ mb: 1.5, display: 'block' }}>
                            {text?.['phone_validation_title'] || 'אימות טלפון'}
                        </TextHot>
                        {loginMessage && (
                            <TextHot size="xxxl" color="secondary" isLtr={false} style={{direction:"rtl"}} sx={{ display: 'block', mb: 1.5, direction: 'rtl'}}>
                                {loginMessage}
                            </TextHot>
                        )}
                        <TextHot size="xxxl" color="secondary" sx={{ display: 'block' }}>
                            {text?.['phone_validation_subtitle'] || 'SMS עם קוד אימות בדרך אליך, הזן אותו כאן'}
                        </TextHot>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: isMobile ? '12px' : '16px',
                            mb: 2,
                            direction: 'ltr',
                            unicodeBidi: 'bidi-override'
                        }}>
                            {otpDigits.map((digit, index) => (
                                <OtpInputHot
                                    key={index}
                                    id={`otp-${index}`}
                                    index={index}
                                    visualIndex={5 - index}
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onPaste={(e) => handlePaste(e, index)}
                                    onFocus={() => setFocusedIndex(index)}
                                    inputRef={(el) => (inputRefs.current[index] = el)}
                                    error={!firstTry && !!otpError}
                                />
                            ))}
                        </Box>
                        {!firstTry && otpError && (
                            <TextHot size="m" color="error" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
                                {otpError}
                            </TextHot>
                        )}
                    </Box>

                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                        <ButtonHot
                            type="submit"
                            mode="contained"
                            size={isMobile ? 'l' : 'xl'}
                            loading={isValidateOtpLoading}
                            disabled={isLoading || isValidateOtpLoading || disableMainBtn || isProcessingVerification}
                        >
                            {text?.['phone_validation_btn'] || 'אמת'}
                        </ButtonHot>
                    </Box>

                    {!lockDown && (
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                <Link
                                    component="button" type="button"
                                    onClick={() => handleResendOtp()}
                                    disabled={!sendTimer && !maxOtpAttemptsReached}
                                    sx={{
                                        color: (sendTimer || maxOtpAttemptsReached) ? colors.system.text.primary : colors.palette.blueAlpha200,
                                        textDecoration: 'underline',
                                        fontSize: isMobile ? '16px' : '18px',
                                        cursor: (sendTimer || maxOtpAttemptsReached) ? 'pointer' : 'not-allowed'
                                    }}
                                >
                                    {text?.['phone_validation_resend'] || 'שלח קוד נוסף'}
                                </Link>
                                <Link
                                    component="button" type="button"
                                    onClick={() => handleResendOtp({ isWhatsappNeeded: true })}
                                    disabled={!sendTimer && !maxOtpAttemptsReached}
                                    sx={{
                                        color: (sendTimer || maxOtpAttemptsReached) ? colors.palette.green500 : colors.palette.blueAlpha200,
                                        textDecoration: 'none',
                                        fontSize: isMobile ? '16px' : '18px',
                                        cursor: (sendTimer || maxOtpAttemptsReached) ? 'pointer' : 'not-allowed',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    {text?.['phone_validation_send_again_whatsapp'] || 'שלחו לי קוד ב-whatsapp'}
                                    <Box
                                        component="img"
                                        src={whatsappIcon}
                                        alt=""
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            opacity: (sendTimer || maxOtpAttemptsReached) ? 1 : 0.4
                                        }}
                                    />
                                </Link>
                                {canSendEmailOtp && (
                                    <Link
                                        component="button" type="button"
                                        onClick={() => handleResendOtp({ isEmailNeeded: true })}
                                        disabled={!sendTimer && !maxOtpAttemptsReached}
                                        sx={{
                                            color: (sendTimer || maxOtpAttemptsReached) ? colors.system.text.primary : colors.palette.blueAlpha200,
                                            textDecoration: 'underline',
                                            fontSize: isMobile ? '16px' : '18px'
                                        }}
                                    >
                                        {text?.['phone_validation_send_again_email'] || text?.['phone_validation_email'] || 'שלח לי קוד במייל'}
                                    </Link>
                                )}
                            </Box>
                        </Box>
                    )}

                    {lockDown && (
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <TextHot size={isMobile ? 'm' : 'l'} color="error">
                                {lockDown.content}
                            </TextHot>
                        </Box>
                    )}
                    <Recaptcha isFunction={true} enterprise={true} onReady={() => {}} />
                </Box>
                {generateOtpErrorModalOpen && <GeneralModal
                    open={generateOtpErrorModalOpen}
                    onClose={() => setGenerateOtpErrorModalOpen(false)}
                    buttonText="אישור"
                    testID={generateTestId(testID, 'generate-otp-error-modal')}
                >
                    {generateOtpErrorMessage}
                </GeneralModal>}
                {validateOtpErrorModalOpen && <GeneralModal
                    open={validateOtpErrorModalOpen}
                    onClose={() => setValidateOtpErrorModalOpen(false)}
                    buttonText="אישור"
                    testID={generateTestId(testID, 'validate-otp-error-modal')}
                >
                    {validateOtpErrorMessage}
                </GeneralModal>}
            </ThemeProvider>
        </ModalHot>
    );
};

export default PhoneValidationModal;